import { useEffect, type RefObject } from 'react';

/** JS-driven horizontal marquee — pauses on hover/focus. */
export function useHorizontalMarquee(
  trackRef: RefObject<HTMLDivElement | null>,
  marqueeRef: RefObject<HTMLDivElement | null>,
  active: boolean,
  options: {
    groupSelector: string;
    trackActiveClass: string;
    speedPxPerSec?: number;
  },
) {
  const { groupSelector, trackActiveClass, speedPxPerSec = 48 } = options;

  useEffect(() => {
    if (!active) return;

    const track = trackRef.current;
    const marquee = marqueeRef.current;
    if (!track) return;

    const firstGroup = track.querySelector(groupSelector) as HTMLElement | null;
    if (!firstGroup) return;

    let offset = 0;
    let paused = false;
    let raf = 0;
    let lastTime = 0;

    const pause = () => { paused = true; };
    const resume = () => { paused = false; lastTime = 0; };

    marquee?.addEventListener('mouseenter', pause);
    marquee?.addEventListener('mouseleave', resume);
    marquee?.addEventListener('focusin', pause);
    marquee?.addEventListener('focusout', resume);

    const step = (time: number) => {
      if (!lastTime) lastTime = time;
      const dt = Math.min((time - lastTime) / 1000, 0.05);
      lastTime = time;

      if (!paused) {
        const groupWidth = firstGroup.offsetWidth;
        if (groupWidth > 0) {
          offset += speedPxPerSec * dt;
          if (offset >= groupWidth) offset -= groupWidth;
          track.style.transform = `translate3d(-${offset}px, 0, 0)`;
        }
      }

      raf = window.requestAnimationFrame(step);
    };

    track.classList.add(trackActiveClass);
    raf = window.requestAnimationFrame(step);

    return () => {
      window.cancelAnimationFrame(raf);
      marquee?.removeEventListener('mouseenter', pause);
      marquee?.removeEventListener('mouseleave', resume);
      marquee?.removeEventListener('focusin', pause);
      marquee?.removeEventListener('focusout', resume);
      track.classList.remove(trackActiveClass);
      track.style.transform = '';
    };
  }, [active, trackRef, marqueeRef, groupSelector, trackActiveClass, speedPxPerSec]);
}
